import {
  type AchBankActivity,
  type EftBankActivity,
  type WireBankActivity,
  BankAccountType,
  BankActivityTypes,
  useBankActivity,
  useWealthOverview,
  WireDirection,
} from '@egw/api'
import { Field, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Currency, dateFormatExtended } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import { useEffect, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { getLocation, getPendingStatus, PendingStatusType } from '../../utils'

export interface TransactionDetailsProps {
  alias: number
  transactionId?: string
}

export const TransactionDetails: FC<TransactionDetailsProps> = ({ alias, transactionId }) => {
  const { t } = useTranslation('features-transactions')
  const navigate = useNavigate()

  const { data: wealthOverviewData } = useWealthOverview()

  const accounts = [
    ...(wealthOverviewData?.highYieldChecking?.accounts ?? []),
    ...(wealthOverviewData?.highYieldSavings?.accounts ?? []),
  ]

  const account = accounts.find((curr) => curr.alias === alias)

  const { data, isLoading, error } = useBankActivity({ alias })
  const transaction = data?.activity.find((item) => item.activityId === transactionId)

  const pendingStatus = transaction && getPendingStatus(transaction)

  const backLinkUrl =
    account?.accountType === BankAccountType.HIGH_YIELD_CHECKING
      ? (WEALTH_ROUTES.CHECKING_ACCOUNT_ACTIVITY_DETAILS.backPath?.replace(':alias', alias.toString()) ?? '')
      : (WEALTH_ROUTES.SAVINGS_ACCOUNT_ACTIVITY_DETAILS.backPath?.replace(':alias', alias.toString()) ?? '')

  useEffect(() => {
    // If WealthOverview Data has returned and there are no accounts, navigate to the overview page
    if (wealthOverviewData && account === undefined) {
      void navigate(WEALTH_ROUTES.OVERVIEW.path)
    }
  }, [account, wealthOverviewData, navigate])

  return (
    <>
      {!isLoading && (
        <PageHeader
          title={account?.accountName}
          subtitle={
            account?.accountType === BankAccountType.HIGH_YIELD_CHECKING
              ? (wealthOverviewData?.highYieldChecking?.productName ?? '')
              : (wealthOverviewData?.highYieldSavings?.productName ?? '')
          }
          className="pb-2xs"
          backLinkUrl={backLinkUrl}
        />
      )}

      <div className="egw-container pt-0">
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {data && transaction ? (
          <>
            <h1 className="text-header font-light text-3xl capitalize">{transaction.displayName}</h1>

            <div className="my-6">
              <span id="transaction_method" className="px-3 py-1 text-gray-500 rounded-full border border-gray-500">
                {transaction.label}
              </span>
            </div>

            <div className="inline-flex items-baseline justify-start gap-2">
              <div id="transaction_amount" className="flex items-baseline justify-start">
                <div className="mt-3 text-4xl font-semibold text-header">
                  {Currency.onlyInteger.plusMinus(transaction.amount.amount)}
                </div>
                <div
                  className={cn('text-2xl font-light', {
                    'text-header': transaction.amount.amount > 0,
                    'text-meta': transaction.amount.amount <= 0,
                  })}
                >
                  {Currency.onlyCents.omitSign(transaction.amount.amount)}
                </div>
              </div>
              <div
                className={cn('font-bold', {
                  'text-header': transaction.amount.amount > 0,
                  'text-meta': transaction.amount.amount <= 0,
                })}
              >
                {transaction.amount.currencyCode}
              </div>
            </div>

            {pendingStatus ? (
              <div
                className={cn('mt-8 rounded-lg py-4 px-8', {
                  'bg-alertWarningBg': pendingStatus.type === PendingStatusType.WARNING,
                  'bg-alertSuccessBg': pendingStatus.type === PendingStatusType.SUCCESS,
                })}
              >
                <p
                  className={cn('font-bold mb-2', {
                    'text-alertWarningTitle': pendingStatus.type === PendingStatusType.WARNING,
                    'text-alertSuccessTitle': pendingStatus.type === PendingStatusType.SUCCESS,
                  })}
                >
                  {t(`activity.pending_banner.${pendingStatus.content}.title`)}
                </p>
                <p
                  className={cn({
                    'text-alertWarningText': pendingStatus.type === PendingStatusType.WARNING,
                    'text-alertSuccessText': pendingStatus.type === PendingStatusType.SUCCESS,
                  })}
                >
                  {t(`activity.pending_banner.${pendingStatus.content}.content`)}
                </p>
              </div>
            ) : null}

            {/* Transaction Notes */}
            <div className="flex flex-col mt-8 gap-4">
              {transaction.__typename === BankActivityTypes.EftBankActivity &&
              (transaction as EftBankActivity).acceptor ? (
                <div className="border-b pb-4">
                  <Field
                    label={t('activity.transaction_details.merchant')}
                    value={(transaction as EftBankActivity).acceptor ?? ''}
                  />
                </div>
              ) : null}

              {transaction.__typename === BankActivityTypes.AchBankActivity &&
              (transaction as AchBankActivity).finInstOrMerchantName ? (
                <div className="border-b pb-4">
                  <Field
                    label={t('activity.transaction_details.merchant')}
                    value={(transaction as AchBankActivity).finInstOrMerchantName ?? ''}
                  />
                </div>
              ) : null}

              {transaction.__typename === BankActivityTypes.EftBankActivity &&
              (transaction as EftBankActivity).location ? (
                <div className="border-b pb-4">
                  <Field
                    label={t('activity.transaction_details.location')}
                    value={getLocation(transaction as EftBankActivity)}
                  />
                </div>
              ) : null}

              {transaction.isProcessing && transaction.date ? (
                <div className="border-b pb-4">
                  <Field
                    label={t('activity.transaction_details.initiated')}
                    value={dateFormatExtended(transaction.date)}
                  />
                </div>
              ) : null}

              {transaction.__typename === BankActivityTypes.WireBankActivity &&
              (transaction as WireBankActivity).direction !== WireDirection.UNKNOWN &&
              (transaction as WireBankActivity).institutionName ? (
                <div className="border-b pb-4">
                  <Field
                    label={t(
                      `activity.transaction_details.recipient_bank.${(transaction as WireBankActivity).direction}`,
                    )}
                    value={(transaction as WireBankActivity).institutionName}
                  />
                </div>
              ) : null}

              {transaction.note ? (
                <div className="border-b pb-4">
                  <Field label={t('activity.transaction_details.memo')} value={transaction.note} />
                </div>
              ) : null}

              {transaction.__typename === BankActivityTypes.WireBankActivity &&
              (transaction as WireBankActivity).imad ? (
                <Field
                  label={t('activity.transaction_details.imad')}
                  value={(transaction as WireBankActivity).imad ?? ''}
                />
              ) : (
                <Field label={t('activity.transaction_details.id')} value={transaction.activityId} />
              )}
            </div>
          </>
        ) : null}

        {error ? (
          <>
            <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
            <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
            <p className="text-body text-xl">{t('activity.error')}</p>
          </>
        ) : null}
      </div>
    </>
  )
}
